import { apiClient, type ManualCommandConfig } from '@/services/http/client'
import { id, object, digest, type Data } from './workflow'
import { inspect, prewriteRefusal } from './sourceReview'
const root = '/legal-source-reviews',
  manual = { manualCommand: true } as ManualCommandConfig
function exact(v: Data, keys: string[]) {
  if (Object.keys(v).sort().join('|') !== [...keys].sort().join('|'))
    throw new Error('Exact source proposal record unavailable')
}
function hash(v: unknown): string {
  if (typeof v !== 'string' || !digest.test(v))
    throw new Error('Exact proposal fingerprint required')
  return v
}
function date(v: unknown): string {
  if (typeof v !== 'string' || !Number.isFinite(Date.parse(v)))
    throw new Error('Exact proposal expiry required')
  return new Date(v).toISOString()
}
function target(v: unknown): Data {
  const t = object(v)
  exact(t, [
    'id',
    'tenant_id',
    'customer_id',
    'subject_type',
    'period_basis',
    'period_value',
    'current_revision',
    'archived',
  ])
  id(t.id)
  id(t.tenant_id)
  if (
    t.subject_type !== 'HUF' ||
    t.archived !== false ||
    !Number.isInteger(t.current_revision) ||
    Number(t.current_revision) < 1 ||
    Number(t.current_revision) > 2147483647
  )
    throw new Error('Active HUF source required')
  for (const [key, max] of [
    ['customer_id', 100],
    ['period_basis', 100],
    ['period_value', 20],
  ] as const)
    if (typeof t[key] !== 'string' || !(t[key] as string).length || (t[key] as string).length > max)
      throw new Error('Exact source labels unavailable')
  return t
}
export interface Query {
  review_id: string
  anchor_request_id: string
  case_id: string
  firm_id: string
}
export interface Context extends Query {
  client_id: string
  subject_id: string
  history_sha256: string
  source_sha256: string
  context_sha256: string
  target: Data
  authority_granted: false
}
export function context(value: unknown, q: Query): Context {
  const v = object(value)
  exact(v, [
    'anchor_request_id',
    'client_id',
    'subject_id',
    'history_sha256',
    'authority_granted',
    'review_id',
    'target',
    'source_sha256',
    'context_sha256',
  ])
  const t = target(v.target)
  if (
    v.authority_granted !== false ||
    id(v.review_id) !== id(q.review_id) ||
    id(v.anchor_request_id) !== id(q.anchor_request_id) ||
    id(t.id) !== id(q.case_id) ||
    id(t.tenant_id) !== id(q.firm_id)
  )
    throw new Error('Original selected source differs')
  id(v.client_id)
  id(v.subject_id)
  hash(v.history_sha256)
  hash(v.source_sha256)
  hash(v.context_sha256)
  return { ...v, ...q, target: t } as Context
}
export async function preview(q: Query): Promise<Context> {
  return context((await apiClient.post(root + '/contexts', q, manual)).data, q)
}
export interface Command extends Query {
  expected_context_sha256: string
  evidence_reference: string
  evidence_sha256: string
  expires_at: string
  acknowledge_review_only: true
}
export async function fingerprint(c: Command): Promise<string> {
  const ordered = Object.fromEntries(
    Object.entries({ ...c, expires_at: date(c.expires_at) }).sort(([a], [b]) => a.localeCompare(b))
  )
  const result = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(JSON.stringify(ordered))
  )
  return Array.from(new Uint8Array(result), b => b.toString(16).padStart(2, '0')).join('')
}
export interface Intent {
  review: string
  context: string
  fingerprint: string
  user: string
  session: string
}
const key = (user: string) => 'taxhive:source-proposal-intent:v1:' + id(user)
export function pending(user: string): Intent | null {
  const raw = localStorage.getItem(key(user))
  if (!raw) return null
  if (raw.length > 1024) throw new Error('Original proposal intent unavailable')
  const v = object(JSON.parse(raw))
  exact(v, ['review', 'context', 'fingerprint', 'user', 'session'])
  id(v.review)
  id(v.session)
  hash(v.context)
  hash(v.fingerprint)
  if (id(v.user) !== id(user)) throw new Error('Original proposal account differs')
  return v as unknown as Intent
}
export function remember(i: Intent) {
  if (pending(i.user)) throw new Error('Recover the original proposal first')
  const raw = JSON.stringify(i)
  localStorage.setItem(key(i.user), raw)
  if (localStorage.getItem(key(i.user)) !== raw)
    throw new Error('Original intent could not be saved')
}
export function forget(user: string) {
  localStorage.removeItem(key(user))
}
export async function send(c: Command) {
  await apiClient.post(root + '/proposals', c, manual)
}
export function refusedBeforeWrite(error: unknown): boolean {
  if (prewriteRefusal(error)) return true
  if (!error || typeof error !== 'object' || !('response' in error)) return false
  if (!error.response || typeof error.response !== 'object' || Array.isArray(error.response))
    return false
  const response = object(error.response),
    data = response.data && typeof response.data === 'object' ? object(response.data) : null
  const detail = data?.detail && typeof data.detail === 'object' ? object(data.detail) : null
  return (
    response.status === 409 &&
    detail?.code === 'PROPOSAL_CONTEXT_CHANGED' &&
    detail.request_recorded === false &&
    detail.authority_granted === false
  )
}
export async function recover(i: Intent): Promise<'matched' | 'conflict' | 'unresolved'> {
  const base = await inspect(i.review)
  const v = object((await apiClient.get(root + '/' + id(i.review) + '/provenance', manual)).data)
  exact(v, ['review_id', 'proposal_observation', 'authority_granted'])
  if (id(v.review_id) !== id(i.review) || v.authority_granted !== false)
    throw new Error('Original proposal provenance differs')
  if (v.proposal_observation === null) return 'conflict'
  const p = object(v.proposal_observation),
    o = object(p.observation),
    q = object(o.query)
  exact(p, ['review_id', 'client_id', 'subject_id', 'context_sha256', 'observation', 'recorded_at'])
  exact(o, [
    'query',
    'facts',
    'source_sha256',
    'checked_at',
    'reviewer_user_id',
    'reviewer_session_id',
    'inspection_audit_id',
    'authority',
    'authority_granted',
    'reservation_held',
    'legal_subject_verified',
    'evidence_verified',
  ])
  exact(q, ['review_id', 'case_id', 'firm_id'])
  id(p.client_id)
  id(p.subject_id)
  date(p.recorded_at)
  date(o.checked_at)
  id(o.inspection_audit_id)
  hash(o.source_sha256)
  hash(p.context_sha256)
  const facts = target(o.facts)
  if (
    id(p.review_id) !== id(i.review) ||
    id(q.review_id) !== id(i.review) ||
    id(q.case_id) !== id(facts.id) ||
    id(q.firm_id) !== id(facts.tenant_id) ||
    o.authority !== 'tax_proposal_source_observation_only' ||
    ['authority_granted', 'reservation_held', 'legal_subject_verified', 'evidence_verified'].some(
      k => o[k] !== false
    )
  )
    throw new Error('Exact observed proposal unavailable')
  for (const k of Object.keys(facts))
    if (facts[k] !== base.command.target[k]) throw new Error('Recorded proposal source differs')
  const cmd: Command = {
    review_id: id(i.review),
    anchor_request_id: id(base.command.anchor_request_id),
    case_id: id(q.case_id),
    firm_id: id(q.firm_id),
    expected_context_sha256: hash(p.context_sha256),
    evidence_reference: base.command.evidence_reference,
    evidence_sha256: base.command.evidence_sha256,
    expires_at: base.command.expires_at,
    acknowledge_review_only: true,
  }
  return p.context_sha256 === i.context &&
    base.user_id === i.user &&
    base.session_id === i.session &&
    id(o.reviewer_user_id) === i.user &&
    id(o.reviewer_session_id) === i.session &&
    (await fingerprint(cmd)) === i.fingerprint
    ? 'matched'
    : 'conflict'
}
